import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import Navigation from "@/components/Navigation";
import Breadcrumb from "@/components/Breadcrumb";
import ChineseZodiacIcon from "@/components/ChineseZodiacIcon";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";

const HoroscopoChino = () => {
  const navigate = useNavigate();
  const [selectedSign, setSelectedSign] = useState("caballo");

  const chineseZodiacSigns = [
    { id: "rata", name: "Rata", years: "1924, 1936, 1948, 1960, 1972, 1984, 1996, 2008, 2020", element: "Agua" },
    { id: "buey", name: "Buey", years: "1925, 1937, 1949, 1961, 1973, 1985, 1997, 2009, 2021", element: "Tierra" },
    { id: "tigre", name: "Tigre", years: "1926, 1938, 1950, 1962, 1974, 1986, 1998, 2010, 2022", element: "Madera" },
    { id: "conejo", name: "Conejo", years: "1927, 1939, 1951, 1963, 1975, 1987, 1999, 2011, 2023", element: "Madera" },
    { id: "dragon", name: "Dragón", years: "1928, 1940, 1952, 1964, 1976, 1988, 2000, 2012, 2024", element: "Tierra" },
    { id: "serpiente", name: "Serpiente", years: "1929, 1941, 1953, 1965, 1977, 1989, 2001, 2013, 2025", element: "Fuego" },
    { id: "caballo", name: "Caballo", years: "1930, 1942, 1954, 1966, 1978, 1990, 2002, 2014, 2026", element: "Fuego", current: true },
    { id: "cabra", name: "Cabra", years: "1931, 1943, 1955, 1967, 1979, 1991, 2003, 2015, 2027", element: "Tierra" },
    { id: "mono", name: "Mono", years: "1932, 1944, 1956, 1968, 1980, 1992, 2004, 2016, 2028", element: "Metal" },
    { id: "gallo", name: "Gallo", years: "1933, 1945, 1957, 1969, 1981, 1993, 2005, 2017, 2029", element: "Metal" },
    { id: "perro", name: "Perro", years: "1934, 1946, 1958, 1970, 1982, 1994, 2006, 2018, 2030", element: "Tierra" },
    { id: "cerdo", name: "Cerdo", years: "1935, 1947, 1959, 1971, 1983, 1995, 2007, 2019, 2031", element: "Agua" },
  ];

  const getElementInfo = (element: string) => {
    const elements: Record<string, { emoji: string; color: string; luckyColor: string }> = {
      "Agua": { emoji: "Agua 💧", color: "#60A5FA", luckyColor: "Azul" },
      "Tierra": { emoji: "Tierra 🌍", color: "#A3A375", luckyColor: "Marrón" },
      "Madera": { emoji: "Madera 🌳", color: "#4ADE80", luckyColor: "Verde" },
      "Fuego": { emoji: "Fuego 🔥", color: "#F87171", luckyColor: "Rojo" },
      "Metal": { emoji: "Metal ⚔️", color: "#E2E8F0", luckyColor: "Blanco" }
    };
    return elements[element] || { emoji: element, color: "#9CA3AF", luckyColor: "Gris" };
  };

  const getLuckyNumbers = (sign: string) => {
    const numbers: Record<string, string> = {
      rata: "2, 3, 6",
      buey: "1, 4, 9",
      tigre: "1, 3, 4",
      conejo: "3, 4, 6",
      dragon: "1, 6, 7",
      serpiente: "2, 8, 9",
      caballo: "2, 3, 7",
      cabra: "2, 7, 8",
      mono: "4, 9, 1",
      gallo: "5, 7, 8",
      perro: "3, 4, 9",
      cerdo: "2, 5, 8"
    };
    return numbers[sign] || "1, 2, 3";
  };

  const getAnimalDescription = (sign: string) => {
    const descriptions: Record<string, string> = {
      rata: "La Rata es ingeniosa, adaptable y encantadora. En 2026, su astucia natural le ayudará a navegar los desafíos del año del Caballo, encontrando oportunidades donde otros ven obstáculos.",
      buey: "El Buey representa la perseverancia, la honestidad y la determinación. En 2026, su estabilidad será un ancla en medio de la energía dinámica del Caballo de Fuego.",
      tigre: "El Tigre simboliza valentía, poder y liderazgo. La energía compatible del Caballo en 2026 amplificará su carisma natural y su capacidad de acción.",
      conejo: "El Conejo encarna elegancia, gentileza y diplomacia. Durante 2026, su naturaleza adaptable le permitirá encontrar armonía incluso en tiempos de cambio.",
      dragon: "El Dragón representa poder, suerte y ambición. En 2026, la sinergia con la energía del Caballo traerá éxitos significativos y reconocimiento.",
      serpiente: "La Serpiente simboliza sabiduría, intuición y misterio. El año del Caballo de Fuego potenciará su capacidad de transformación personal.",
      caballo: "¡Este es TU año! El Caballo representa libertad, pasión y energía. En 2026, estarás en el centro del escenario con oportunidades extraordinarias.",
      cabra: "La Cabra encarna creatividad, amabilidad y sensibilidad artística. La energía del Caballo en 2026 te impulsará a expresar tu arte con mayor audacia.",
      mono: "El Mono representa ingenio, curiosidad y versatilidad. En 2026, tu naturaleza adaptable te permitirá aprovechar múltiples oportunidades.",
      gallo: "El Gallo simboliza confianza, honestidad y trabajo arduo. El año del Caballo de Fuego reconocerá tus esfuerzos y dedicación.",
      perro: "El Perro encarna lealtad, honestidad y justicia. En 2026, tu naturaleza fiel será recompensada con relaciones más profundas.",
      cerdo: "El Cerdo representa generosidad, compasión y fortuna. La energía del Caballo en 2026 traerá abundancia a tu vida."
    };
    return descriptions[sign] || "Descubre tu camino en el año del Caballo de Fuego.";
  };

  const predictions2026: Record<string, { amor: string; trabajo: string; salud: string; general: string }> = {
    rata: {
      general: "2026, el Año del Caballo de Fuego, trae energías opuestas para la Rata. Será un año de desafíos que fortalecerán tu carácter y te obligarán a salir de tu zona de confort.",
      amor: "Las relaciones requerirán mayor compromiso y comunicación. Los solteros encontrarán conexiones inesperadas en eventos sociales. La pasión del Caballo puede encender romances intensos.",
      trabajo: "Tu ingenio será clave para superar obstáculos laborales. Evita conflictos con superiores y enfócate en alianzas estratégicas. Nuevas oportunidades surgirán en el segundo semestre.",
      salud: "Cuida tu sistema nervioso y evita el estrés excesivo. La meditación y el ejercicio moderado serán tus mejores aliados. Presta atención a tu alimentación."
    },
    buey: {
      general: "El Buey encontrará en 2026 un año de crecimiento estable. La energía del Caballo te impulsará a moverte más rápido de lo habitual, trayendo cambios positivos.",
      amor: "La estabilidad emocional será tu fortaleza. Las parejas consolidarán su relación con compromisos importantes. Los solteros atraerán personas que valoran la lealtad.",
      trabajo: "Tu perseverancia dará frutos este año. Proyectos a largo plazo mostrarán resultados tangibles. Posibilidades de ascenso o reconocimiento profesional.",
      salud: "Excelente vitalidad durante todo el año. Aprovecha para establecer rutinas saludables que perdurarán. Cuida tus articulaciones con ejercicio regular."
    },
    tigre: {
      general: "2026 será un año de acción y aventura para el Tigre. La compatibilidad con la energía del Caballo potenciará tu carisma natural y abrirá nuevas puertas.",
      amor: "Romance apasionado y emocionante te espera. Las relaciones existentes se revitalizarán con aventuras compartidas. Solteros vivirán encuentros memorables.",
      trabajo: "Tu liderazgo será reconocido. Oportunidades para emprender o liderar proyectos innovadores. El dinamismo del año favorece tus ambiciones.",
      salud: "Energía abundante pero cuida no agotarte. Deportes de aventura y actividades al aire libre serán beneficiosos. Mantén el equilibrio entre actividad y descanso."
    },
    conejo: {
      general: "El Conejo navegará 2026 con diplomacia y elegancia. Aunque la energía del Caballo puede resultar intensa, tu adaptabilidad te permitirá prosperar.",
      amor: "Año favorable para la armonía en pareja. La comunicación fluida fortalecerá vínculos. Solteros podrían encontrar el amor en ambientes culturales o artísticos.",
      trabajo: "Tu creatividad será valorada. Evita conflictos y busca soluciones pacíficas. Proyectos artísticos o relacionados con el diseño florecerán.",
      salud: "Cuida tu sensibilidad emocional. Prácticas como el yoga y la meditación serán especialmente beneficiosas. Alimentación equilibrada es clave."
    },
    dragon: {
      general: "El Dragón brillará en 2026 con la energía compatible del Caballo. Será un año de logros significativos y reconocimiento merecido.",
      amor: "Magnetismo personal en su punto máximo. Las relaciones serán apasionadas e intensas. Posibilidad de compromisos importantes o nuevos comienzos románticos.",
      trabajo: "Éxito en emprendimientos y proyectos ambiciosos. Tu carisma atraerá inversores y colaboradores. Año ideal para expandir horizontes profesionales.",
      salud: "Vitalidad excepcional pero no descuides el descanso. Actividades que combinen ejercicio físico con desarrollo personal serán ideales."
    },
    serpiente: {
      general: "La Serpiente encontrará en 2026 oportunidades para demostrar su sabiduría. La energía de Fuego complementa tu intuición natural.",
      amor: "Conexiones profundas y significativas. Las relaciones evolucionarán hacia mayor intimidad emocional. Solteros atraerán personas con profundidad intelectual.",
      trabajo: "Tu visión estratégica será tu mayor activo. Inversiones inteligentes darán buenos frutos. Posibilidades en campos de investigación o consultoría.",
      salud: "Presta atención a tu bienestar emocional. Prácticas de mindfulness y tiempo en la naturaleza renovarán tu energía. Cuida tu piel y sistema digestivo."
    },
    caballo: {
      general: "¡Es TU AÑO! 2026 es el Año del Caballo de Fuego, trayendo tu energía al centro del escenario. Prepárate para un año de transformación, libertad y nuevos comienzos.",
      amor: "Romance apasionado y aventurero te espera. Si estás en pareja, renacerá la llama. Los solteros tendrán múltiples oportunidades para encontrar conexiones especiales.",
      trabajo: "Tu momento para brillar profesionalmente. Nuevas oportunidades, viajes de trabajo y reconocimiento. Ideal para emprender o cambiar de carrera.",
      salud: "Energía desbordante pero cuida no quemarte. Deportes y actividades físicas te mantendrán equilibrado. Atiende tu corazón y sistema circulatorio."
    },
    cabra: {
      general: "La Cabra encontrará armonía en 2026. La energía del Caballo te impulsará a ser más audaz mientras mantienes tu naturaleza artística.",
      amor: "Año de romanticismo y ternura. Las relaciones se profundizarán con gestos creativos de amor. Solteros podrían encontrar almas afines en actividades artísticas.",
      trabajo: "Tu creatividad será tu mayor fortaleza. Proyectos artísticos florecerán. Colaboraciones beneficiosas con signos de Fuego.",
      salud: "Bienestar emocional como prioridad. Expresión artística como terapia. Cuida tu sistema digestivo y mantén rutinas regulares."
    },
    mono: {
      general: "El Mono disfrutará un año dinámico y lleno de oportunidades. La energía del Caballo amplifica tu ingenio natural.",
      amor: "Socialización activa traerá nuevas conexiones. Relaciones existentes se beneficiarán de aventuras compartidas. Diversión y romance van de la mano.",
      trabajo: "Tu adaptabilidad será clave para el éxito. Múltiples proyectos pueden prosperar simultáneamente. Networking activo abrirá puertas.",
      salud: "Mantén el equilibrio entre actividad mental y física. Tu mente activa necesita descanso. Ejercicios que desafíen cuerpo y mente serán ideales."
    },
    gallo: {
      general: "2026 traerá al Gallo oportunidades para brillar. La energía de Fuego potencia tu confianza natural y atrae reconocimiento.",
      amor: "Tu carisma atraerá admiradores. Las relaciones se beneficiarán de tu dedicación. Solteros encontrarán parejas que aprecien su autenticidad.",
      trabajo: "Reconocimiento profesional y posibles ascensos. Tu perfeccionismo será valorado. Proyectos de liderazgo están favorecidos.",
      salud: "Excelente vitalidad general. Cuida tu garganta y sistema respiratorio. Actividades al aire libre temprano en la mañana serán especialmente beneficiosas."
    },
    perro: {
      general: "El Perro encontrará estabilidad y lealtad recompensada en 2026. La energía del Caballo te animará a explorar nuevas experiencias.",
      amor: "Fidelidad y compromiso definen tu año romántico. Las relaciones sólidas se fortalecerán. Solteros atraerán parejas sinceras y confiables.",
      trabajo: "Tu ética laboral será reconocida. Oportunidades para asumir responsabilidades mayores. Proyectos de equipo prosperarán bajo tu guía.",
      salud: "Buena salud general pero no descuides los chequeos rutinarios. Actividades al aire libre con amigos o mascotas renovarán tu espíritu."
    },
    cerdo: {
      general: "El Cerdo disfrutará un año de abundancia y placer en 2026. La energía del Caballo trae movimiento a tu vida cómoda.",
      amor: "Generosidad y afecto caracterizan tu vida amorosa. Las relaciones florecerán con gestos románticos. Solteros atraerán parejas que valoren la autenticidad.",
      trabajo: "Prosperidad financiera y estabilidad profesional. Tu naturaleza generosa creará alianzas valiosas. Inversiones seguras darán buenos rendimientos.",
      salud: "Cuida los excesos alimentarios. Equilibrio entre placer y moderación. Actividades grupales y sociales beneficiarán tu bienestar emocional."
    }
  };

  const selectedZodiac = chineseZodiacSigns.find(sign => sign.id === selectedSign);
  const selectedPrediction = predictions2026[selectedSign];

  return (
    <>
      <Helmet>
        <title>Horóscopo Chino 2026 - Año del Caballo de Fuego | Dstino</title>
        <meta name="description" content="Horóscopo chino 2026, Año del Caballo de Fuego. Predicciones para los 12 signos del zodiaco chino: Rata, Buey, Tigre, Conejo, Dragón, Serpiente, Caballo, Cabra, Mono, Gallo, Perro y Cerdo." />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Navigation />
        
        <main className="container mx-auto px-4 pt-24 pb-12">
          <Breadcrumb 
            items={[
              { name: "Inicio", url: "/" },
              { name: "Horóscopo Chino" }
            ]}
          />

          <div className="text-center mb-8">
            <Badge className="mb-4 bg-red-500/20 text-red-400 border-red-500/30">
              🐎 Año del Caballo de Fuego 2026
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-4 text-glow">Horóscopo Chino 2026</h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Descubre las predicciones del zodiaco chino para el Año del Caballo de Fuego. 
              Un año de pasión, libertad y nuevos comienzos.
            </p>
          </div>

          {/* Zodiac Signs Grid */}
          <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4 mb-8">
            {chineseZodiacSigns.map((sign) => (
              <Card 
                key={sign.id}
                className={`cursor-pointer transition-all duration-300 hover:scale-105 relative overflow-hidden ${
                  selectedSign === sign.id 
                    ? 'ring-2 ring-primary shadow-[0_0_20px_rgba(239,68,68,0.3)]' 
                    : 'hover:shadow-lg'
                } ${sign.current ? 'border-red-500/50' : ''}`}
                onClick={() => {
                  setSelectedSign(sign.id);
                }}
              >
                {sign.current && (
                  <div className="absolute top-1 right-1">
                    <span className="text-xs bg-red-500 text-white px-1.5 py-0.5 rounded-full">2026</span>
                  </div>
                )}
                <CardContent className="p-4 text-center">
                  <div className="flex justify-center mb-2">
                    <ChineseZodiacIcon animal={sign.id} size={48} />
                  </div>
                  <h3 className="font-semibold text-sm md:text-base">{sign.name}</h3>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Selected Sign Details */}
          <Card className="max-w-4xl mx-auto gradient-celestial">
            <CardContent className="p-6 md:p-8">
              <div className="text-center mb-6">
                <div className="flex justify-center mb-4">
                  <ChineseZodiacIcon animal={selectedSign} size={96} />
                </div>
                <h2 className="text-3xl font-bold mb-2">{selectedZodiac?.name}</h2>
                <div className="flex flex-wrap justify-center gap-2 mb-3">
                  <Badge variant="outline">{selectedZodiac?.element}</Badge>
                  {selectedZodiac?.current && (
                    <Badge className="bg-red-500 text-white">Signo del Año</Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">Años: {selectedZodiac?.years}</p>
              </div>

              {/* 2026 Prediction Overview */}
              <div className="bg-card/50 rounded-lg p-4 mb-6 border border-border/50">
                <h3 className="font-semibold text-primary mb-2">✨ Predicción 2026</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {selectedPrediction?.general}
                </p>
              </div>

              <Tabs defaultValue="amor" className="w-full" key={selectedSign}>
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="amor">Amor</TabsTrigger>
                  <TabsTrigger value="trabajo">Trabajo</TabsTrigger>
                  <TabsTrigger value="salud">Salud</TabsTrigger>
                </TabsList>

                <TabsContent value="amor" className="space-y-4 pt-4">
                  <div className="bg-card/30 rounded-lg p-4">
                    <h3 className="text-xl font-semibold mb-3 text-primary flex items-center gap-2">
                      💝 Amor en 2026
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {selectedPrediction?.amor}
                    </p>
                  </div>
                </TabsContent>

                <TabsContent value="trabajo" className="space-y-4 pt-4">
                  <div className="bg-card/30 rounded-lg p-4">
                    <h3 className="text-xl font-semibold mb-3 text-primary flex items-center gap-2">
                      💼 Trabajo en 2026
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {selectedPrediction?.trabajo}
                    </p>
                  </div>
                </TabsContent>

                <TabsContent value="salud" className="space-y-4 pt-4">
                  <div className="bg-card/30 rounded-lg p-4">
                    <h3 className="text-xl font-semibold mb-3 text-primary flex items-center gap-2">
                      🏥 Salud en 2026
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {selectedPrediction?.salud}
                    </p>
                  </div>
                </TabsContent>
              </Tabs>

              <div className="text-center mt-6">
                <button
                  onClick={() => navigate(`/horoscopo-${selectedSign}`)}
                  className="text-primary hover:underline text-sm"
                >
                  Ver predicciones diarias del {selectedZodiac?.name} →
                </button>
              </div>
            </CardContent>
          </Card>

          {/* Selected Animal Info */}
          <Card className="max-w-4xl mx-auto mt-8 gradient-mystical" key={`info-${selectedSign}`}>
            <CardContent className="p-6 md:p-8">
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="flex-shrink-0">
                  <ChineseZodiacIcon animal={selectedSign} size={120} />
                </div>
                <div>
                  <h2 className="text-2xl font-bold mb-3">
                    {selectedZodiac?.name} en 2026: Año del Caballo de Fuego
                  </h2>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    {getAnimalDescription(selectedSign)}
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
                    <div className="bg-card/50 rounded-lg p-3">
                      <p className="text-xs text-muted-foreground">Elemento</p>
                      <p className="font-semibold text-red-400">{getElementInfo(selectedZodiac?.element || "").emoji}</p>
                    </div>
                    <div className="bg-card/50 rounded-lg p-3">
                      <p className="text-xs text-muted-foreground">Color</p>
                      <p className="font-semibold" style={{ color: getElementInfo(selectedZodiac?.element || "").color }}>{getElementInfo(selectedZodiac?.element || "").luckyColor}</p>
                    </div>
                    <div className="bg-card/50 rounded-lg p-3">
                      <p className="text-xs text-muted-foreground">Números</p>
                      <p className="font-semibold">{getLuckyNumbers(selectedSign)}</p>
                    </div>
                    <div className="bg-card/50 rounded-lg p-3">
                      <p className="text-xs text-muted-foreground">Dirección</p>
                      <p className="font-semibold">Sur</p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </main>
      </div>
    </>
  );
};

export default HoroscopoChino;
